import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Card,
  DatePicker,
  Select,
  Table,
  message,
  Statistic,
  Row,
  Col,
  Spin,
  Tag,
  Space,
  Tabs,
  Badge,
  Switch,
} from "antd";
import {
  ShoppingOutlined,
  InboxOutlined,
  TeamOutlined,
  BookOutlined,
  LineChartOutlined,
  ContainerOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import { fetchBookingsForExport } from "../services/bookingExportService";
import { Booking } from "../types/booking";
import { supabase } from "../lib/supabase";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

// Styled components
const Container = styled.div`
  padding: 24px;
`;

const DashboardCard = styled(Card)`
  margin-bottom: 24px;
  height: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  margin-bottom: 20px;
`;

const FiltersContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
`;

const StatsCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  height: 100%;
`;

const CenteredSpin = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

// Color constants
const AIRFREIGHT_COLOR = "#1890ff";
const SEAFREIGHT_COLOR = "#52c41a";

interface Company {
  id: number;
  name: string;
}

interface DailyData {
  date: string;
  airfreight: number;
  seafreight: number;
  airfreightCBM: number;
  seafreightCBM: number;
  airfreightKG: number;
  seafreightKG: number;
}

interface TopCustomer {
  company: string;
  bookings: number;
  airfreightBookings: number;
  seafreightBookings: number;
  totalCBM: number;
  totalKG: number;
}

interface RoutePerformance {
  route: string;
  bookings: number;
  totalCBM: number;
  totalKG: number;
}

interface ContainerWithBookings {
  id: number;
  reference: string | null;
  eta: string | null;
  etd: string | null;
  status: string | null;
  unloading_status: string | null;
  unloading_started_at: string | null;
  unloading_completed_at: string | null;
  bookings: Array<{
    id: number;
    reference: string | null;
    status: string | null;
    unloading_status: string | null;
    company_name: string;
    total_products: number;
    has_po: boolean;
    has_shipping_labels: boolean;
    po_count: number;
    shipping_label_count: number;
  }>;
  total_bookings: number;
  total_products: number;
  bookings_with_po: number;
  bookings_with_labels: number;
}

/**
 * Reports Admin Page - Clean Dashboard for Forwarding Business Owner
 */
const ReportsAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [containers, setContainers] = useState<ContainerWithBookings[]>([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);
  const [transportTypeFilter, setTransportTypeFilter] = useState<
    string | undefined
  >(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [companyFilter, setCompanyFilter] = useState<number | undefined>(
    undefined
  );
  const [isStacked, setIsStacked] = useState(false);
  const [groupingPeriod, setGroupingPeriod] = useState<
    "day" | "week" | "month"
  >("day");

  // Date range shortcuts
  const dateRangeShortcuts = {
    Today: [dayjs().startOf("day"), dayjs().endOf("day")],
    "This Week": [dayjs().startOf("week"), dayjs().endOf("week")],
    "Last Week": [
      dayjs().subtract(1, "week").startOf("week"),
      dayjs().subtract(1, "week").endOf("week"),
    ],
    "This Month": [dayjs().startOf("month"), dayjs().endOf("month")],
    "Last Month": [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
    "This Year": [dayjs().startOf("year"), dayjs().endOf("year")],
    "Last Year": [
      dayjs().subtract(1, "year").startOf("year"),
      dayjs().subtract(1, "year").endOf("year"),
    ],
  };

  // Track page view
  useEffect(() => {
    trackFeatureUsage("reports_admin");
  }, []);

  // Auto-refresh for live operations tab - only when tab is active
  useEffect(() => {
    if (activeTab === "live") {
      // Load immediately when tab becomes active
      loadContainers();

      // Refresh every 60 seconds (reduced frequency to improve performance)
      const interval = setInterval(() => {
        if (activeTab === "live") {
          loadContainers();
        }
      }, 60000); // Refresh every 60 seconds

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Fetch companies for filter
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data, error } = await supabase
          .from("company")
          .select("id, name")
          .order("name");

        if (error) throw error;
        setCompanies(data || []);
      } catch (err) {
        console.error("Error fetching companies:", err);
      }
    };

    fetchCompanies();
  }, []);

  // Fetch containers for live operations - OPTIMIZED to avoid N+1 queries
  const loadContainers = async () => {
    setLoadingContainers(true);
    try {
      const today = dayjs().startOf("day");
      const endOfWeek = dayjs().endOf("week");

      // Fetch containers coming in this week
      const { data: containersThisWeek, error: weekError } = await supabase
        .from("containers")
        .select(
          "id, reference, eta, etd, status, unloading_status, unloading_started_at, unloading_completed_at"
        )
        .gte("eta", today.format("YYYY-MM-DD"))
        .lte("eta", endOfWeek.format("YYYY-MM-DD"))
        .limit(50); // Limit to prevent too many containers

      // Fetch containers currently being unloaded
      const { data: containersUnloading, error: unloadingError } =
        await supabase
          .from("containers")
          .select(
            "id, reference, eta, etd, status, unloading_status, unloading_started_at, unloading_completed_at"
          )
          .in("unloading_status", ["in_progress", "pending"])
          .limit(50);

      if (weekError) throw weekError;
      if (unloadingError) throw unloadingError;

      // Combine and deduplicate by container id
      const allContainers = [
        ...(containersThisWeek || []),
        ...(containersUnloading || []),
      ];
      const uniqueContainers = Array.from(
        new Map(allContainers.map((c) => [c.id, c])).values()
      ).sort((a, b) => {
        if (!a.eta && !b.eta) return 0;
        if (!a.eta) return 1;
        if (!b.eta) return -1;
        return dayjs(a.eta).unix() - dayjs(b.eta).unix();
      });

      const containerIds = uniqueContainers.map((c) => c.id);

      // If no containers, set empty array and return early
      if (containerIds.length === 0) {
        setContainers([]);
        setLoadingContainers(false);
        return;
      }

      // OPTIMIZED: Fetch ALL bookings for all containers in a single query
      const { data: allBookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(
          `
          id,
          container_id,
          reference,
          status,
          unloading_status,
          company_info:company(name),
          booking_product(id, shipping_label),
          booking_purchase_orders(id)
        `
        )
        .in("container_id", containerIds);

      if (bookingsError) throw bookingsError;

      // Group bookings by container_id in JavaScript (much faster than N queries)
      const bookingsByContainer = new Map<number, any[]>();
      (allBookingsData || []).forEach((booking: any) => {
        if (!booking.container_id) return;

        const products = Array.isArray(booking.booking_product)
          ? booking.booking_product
          : booking.booking_product
          ? [booking.booking_product]
          : [];

        const companyInfo = Array.isArray(booking.company_info)
          ? booking.company_info[0]
          : booking.company_info;

        const poCount = Array.isArray(booking.booking_purchase_orders)
          ? booking.booking_purchase_orders.length
          : booking.booking_purchase_orders
          ? 1
          : 0;

        const shippingLabelCount = products.filter(
          (p: any) => p.shipping_label
        ).length;

        const bookingData = {
          id: booking.id,
          reference: booking.reference || `EFF${booking.id}`,
          status: booking.status,
          unloading_status: booking.unloading_status,
          company_name: companyInfo?.name || "Unknown",
          total_products: products.length,
          has_po: poCount > 0,
          has_shipping_labels: shippingLabelCount > 0,
          po_count: poCount,
          shipping_label_count: shippingLabelCount,
        };

        if (!bookingsByContainer.has(booking.container_id)) {
          bookingsByContainer.set(booking.container_id, []);
        }
        bookingsByContainer.get(booking.container_id)!.push(bookingData);
      });

      // Combine containers with their bookings
      const containersWithBookings = uniqueContainers.map((container) => {
        const bookings = bookingsByContainer.get(container.id) || [];
        return {
          ...container,
          bookings,
          total_bookings: bookings.length,
          total_products: bookings.reduce(
            (sum, b) => sum + b.total_products,
            0
          ),
          bookings_with_po: bookings.filter((b) => b.has_po).length,
          bookings_with_labels: bookings.filter((b) => b.has_shipping_labels)
            .length,
        };
      });

      setContainers(containersWithBookings);
    } catch (err) {
      console.error("Error loading containers:", err);
      message.error("Failed to load container data");
    } finally {
      setLoadingContainers(false);
    }
  };

  // Fetch bookings
  useEffect(() => {
    loadBookings();
  }, [dateRange, transportTypeFilter, statusFilter, companyFilter]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const startDate = dateRange[0].startOf("day").format("YYYY-MM-DD");
      const endDate = dateRange[1].endOf("day").format("YYYY-MM-DD");

      const { data, error } = await fetchBookingsForExport({
        dateRange: [startDate, endDate] as [string, string],
        status: statusFilter,
        transportTypeId: transportTypeFilter
          ? Number(transportTypeFilter)
          : undefined,
      });

      if (error) throw error;

      // Filter by company if selected
      let filteredData = data || [];
      if (companyFilter) {
        filteredData = filteredData.filter((booking) => {
          const companyInfo = Array.isArray(booking.company_info)
            ? booking.company_info[0]
            : booking.company_info;
          return companyInfo?.id === companyFilter;
        });
      }

      setBookings(filteredData);
    } catch (err) {
      console.error("Error loading booking data:", err);
      message.error("Failed to load booking data");
    } finally {
      setLoading(false);
    }
  };

  // Process bookings into aggregates by selected period (day, week, month)
  const dailyData = useMemo(() => {
    const dataMap = new Map<string, DailyData>();

    bookings.forEach((booking) => {
      if (!booking.created_at) return;

      const bookingDate = dayjs.utc(booking.created_at).local();
      let periodKey: string;
      let periodDate: dayjs.Dayjs;

      if (groupingPeriod === "day") {
        periodKey = bookingDate.format("YYYY-MM-DD");
        periodDate = bookingDate.startOf("day");
      } else if (groupingPeriod === "week") {
        periodDate = bookingDate.startOf("week");
        periodKey = periodDate.format("YYYY-MM-DD");
      } else {
        // month
        periodDate = bookingDate.startOf("month");
        periodKey = periodDate.format("YYYY-MM-DD");
      }

      const transportType = booking.transport_type;

      // Get products
      const products = Array.isArray(booking.product_info)
        ? booking.product_info
        : booking.product_info
        ? [booking.product_info]
        : [];

      const totalCBM = products.reduce((sum, p) => sum + (p.cbm || 0), 0);
      const totalKG = products.reduce(
        (sum, p) => sum + (p.gross_weight || 0),
        0
      );

      if (!dataMap.has(periodKey)) {
        dataMap.set(periodKey, {
          date: periodKey,
          airfreight: 0,
          seafreight: 0,
          airfreightCBM: 0,
          seafreightCBM: 0,
          airfreightKG: 0,
          seafreightKG: 0,
        });
      }

      const periodData = dataMap.get(periodKey)!;

      if (transportType === 1) {
        // Airfreight
        periodData.airfreight += 1;
        periodData.airfreightCBM += totalCBM;
        periodData.airfreightKG += totalKG;
      } else if (transportType === 2) {
        // Seafreight
        periodData.seafreight += 1;
        periodData.seafreightCBM += totalCBM;
        periodData.seafreightKG += totalKG;
      }
    });

    // Fill in missing periods in range
    const result: DailyData[] = [];
    let current = dateRange[0].startOf(
      groupingPeriod === "day"
        ? "day"
        : groupingPeriod === "week"
        ? "week"
        : "month"
    );
    const end = dateRange[1].startOf(
      groupingPeriod === "day"
        ? "day"
        : groupingPeriod === "week"
        ? "week"
        : "month"
    );

    while (
      current.isBefore(end) ||
      current.isSame(
        end,
        groupingPeriod === "day"
          ? "day"
          : groupingPeriod === "week"
          ? "week"
          : "month"
      )
    ) {
      const periodKey = current.format("YYYY-MM-DD");
      const existing = dataMap.get(periodKey);
      result.push(
        existing || {
          date: periodKey,
          airfreight: 0,
          seafreight: 0,
          airfreightCBM: 0,
          seafreightCBM: 0,
          airfreightKG: 0,
          seafreightKG: 0,
        }
      );

      if (groupingPeriod === "day") {
        current = current.add(1, "day");
      } else if (groupingPeriod === "week") {
        current = current.add(1, "week");
      } else {
        current = current.add(1, "month");
      }
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }, [bookings, dateRange, groupingPeriod]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    let totalBookings = 0;
    let totalCBM = 0;
    let totalKG = 0;
    let airfreightBookings = 0;
    let seafreightBookings = 0;
    let airfreightCBM = 0;
    let seafreightCBM = 0;
    let airfreightKG = 0;
    let seafreightKG = 0;

    bookings.forEach((booking) => {
      const transportType = booking.transport_type;
      const products = Array.isArray(booking.product_info)
        ? booking.product_info
        : booking.product_info
        ? [booking.product_info]
        : [];

      const totalCBMBooking = products.reduce(
        (sum, p) => sum + (p.cbm || 0),
        0
      );
      const totalKGBooking = products.reduce(
        (sum, p) => sum + (p.gross_weight || 0),
        0
      );

      totalBookings += 1;
      totalCBM += totalCBMBooking;
      totalKG += totalKGBooking;

      if (transportType === 1) {
        airfreightBookings += 1;
        airfreightCBM += totalCBMBooking;
        airfreightKG += totalKGBooking;
      } else if (transportType === 2) {
        seafreightBookings += 1;
        seafreightCBM += totalCBMBooking;
        seafreightKG += totalKGBooking;
      }
    });

    return {
      totalBookings,
      totalCBM,
      totalKG,
      airfreightBookings,
      seafreightBookings,
      airfreightCBM,
      seafreightCBM,
      airfreightKG,
      seafreightKG,
      avgCBM: totalBookings > 0 ? totalCBM / totalBookings : 0,
      avgKG: totalBookings > 0 ? totalKG / totalBookings : 0,
    };
  }, [bookings]);

  // Top customers
  const topCustomers = useMemo(() => {
    const customerMap = new Map<
      string,
      {
        bookings: number;
        airfreightBookings: number;
        seafreightBookings: number;
        totalCBM: number;
        totalKG: number;
      }
    >();

    bookings.forEach((booking) => {
      const companyInfo = Array.isArray(booking.company_info)
        ? booking.company_info[0]
        : booking.company_info;
      const companyName = companyInfo?.name || "Unknown";
      const transportType = booking.transport_type;

      if (!customerMap.has(companyName)) {
        customerMap.set(companyName, {
          bookings: 0,
          airfreightBookings: 0,
          seafreightBookings: 0,
          totalCBM: 0,
          totalKG: 0,
        });
      }

      const customer = customerMap.get(companyName)!;
      customer.bookings += 1;

      // Track by transport type
      if (transportType === 1) {
        customer.airfreightBookings += 1;
      } else if (transportType === 2) {
        customer.seafreightBookings += 1;
      }

      const products = Array.isArray(booking.product_info)
        ? booking.product_info
        : booking.product_info
        ? [booking.product_info]
        : [];

      customer.totalCBM += products.reduce((sum, p) => sum + (p.cbm || 0), 0);
      customer.totalKG += products.reduce(
        (sum, p) => sum + (p.gross_weight || 0),
        0
      );
    });

    return Array.from(customerMap.entries())
      .map(([company, data]) => ({
        company,
        ...data,
      }))
      .sort((a, b) => b.bookings - a.bookings)
      .slice(0, 10);
  }, [bookings]);

  // Route performance
  const routePerformance = useMemo(() => {
    const routeMap = new Map<
      string,
      { bookings: number; totalCBM: number; totalKG: number }
    >();

    bookings.forEach((booking) => {
      // Try to get origin port from booking first
      let originPort = Array.isArray(booking.origin_port_info)
        ? booking.origin_port_info[0]
        : booking.origin_port_info;

      // If not available, try to get from container
      if (!originPort?.name) {
        const containerInfo = Array.isArray(booking.container_info)
          ? booking.container_info[0]
          : booking.container_info;

        if (containerInfo) {
          // Try container_origin_port first
          if (containerInfo.container_origin_port) {
            const containerOriginPort = Array.isArray(
              containerInfo.container_origin_port
            )
              ? containerInfo.container_origin_port[0]
              : containerInfo.container_origin_port;
            if (containerOriginPort?.name) {
              originPort = containerOriginPort;
            }
          }
          // Fallback to origin_port_info on container
          if (!originPort?.name && containerInfo.origin_port_info) {
            const containerOriginInfo = Array.isArray(
              containerInfo.origin_port_info
            )
              ? containerInfo.origin_port_info[0]
              : containerInfo.origin_port_info;
            if (containerOriginInfo?.name) {
              originPort = containerOriginInfo;
            }
          }
        }
      }

      // Try to get destination port from booking first
      let destPort = Array.isArray(booking.destination_port_info)
        ? booking.destination_port_info[0]
        : booking.destination_port_info;

      // If not available, try to get from container
      if (!destPort?.name) {
        const containerInfo = Array.isArray(booking.container_info)
          ? booking.container_info[0]
          : booking.container_info;

        if (containerInfo) {
          // Try container_destination_port first
          if (containerInfo.container_destination_port) {
            const containerDestPort = Array.isArray(
              containerInfo.container_destination_port
            )
              ? containerInfo.container_destination_port[0]
              : containerInfo.container_destination_port;
            if (containerDestPort?.name) {
              destPort = containerDestPort;
            }
          }
          // Fallback to destination_port_info on container
          if (!destPort?.name && containerInfo.destination_port_info) {
            const containerDestInfo = Array.isArray(
              containerInfo.destination_port_info
            )
              ? containerInfo.destination_port_info[0]
              : containerInfo.destination_port_info;
            if (containerDestInfo?.name) {
              destPort = containerDestInfo;
            }
          }
        }
      }

      const originName = originPort?.name || "Unknown";
      const destName = destPort?.name || "Unknown";

      // Skip if both are unknown
      if (originName === "Unknown" && destName === "Unknown") {
        return;
      }

      const route = `${originName} → ${destName}`;

      if (!routeMap.has(route)) {
        routeMap.set(route, { bookings: 0, totalCBM: 0, totalKG: 0 });
      }

      const routeData = routeMap.get(route)!;
      routeData.bookings += 1;

      const products = Array.isArray(booking.product_info)
        ? booking.product_info
        : booking.product_info
        ? [booking.product_info]
        : [];

      routeData.totalCBM += products.reduce((sum, p) => sum + (p.cbm || 0), 0);
      routeData.totalKG += products.reduce(
        (sum, p) => sum + (p.gross_weight || 0),
        0
      );
    });

    return Array.from(routeMap.entries())
      .map(([route, data]) => ({
        route,
        ...data,
      }))
      .sort((a, b) => b.bookings - a.bookings)
      .slice(0, 10);
  }, [bookings]);

  // Transport type distribution for pie chart
  const transportDistribution = useMemo(() => {
    const airfreight = summaryStats.airfreightBookings;
    const seafreight = summaryStats.seafreightBookings;
    return [
      { name: "Airfreight", value: airfreight },
      { name: "Seafreight", value: seafreight },
    ];
  }, [summaryStats]);

  return (
    <Container>
      <AdminNavBar />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={2}>Reports Dashboard</Title>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <LineChartOutlined /> Analytics
            </span>
          }
          key="analytics"
        >
          <FiltersContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, color: "#8c8c8c" }}>
                Date Range
              </label>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                ranges={dateRangeShortcuts}
                style={{ width: 240 }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, color: "#8c8c8c" }}>
                Transport Type
              </label>
              <Select
                style={{ width: 150 }}
                placeholder="All Types"
                allowClear
                value={transportTypeFilter}
                onChange={setTransportTypeFilter}
              >
                <Option value="1">Airfreight</Option>
                <Option value="2">Seafreight</Option>
              </Select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, color: "#8c8c8c" }}>Status</label>
              <Select
                style={{ width: 150 }}
                placeholder="All Statuses"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
              >
                <Option value="concept">Concept</Option>
                <Option value="submitted">Submitted</Option>
                <Option value="approved">Approved</Option>
                <Option value="waiting_for_goods">Waiting for Goods</Option>
                <Option value="goods_received_origin">Goods Received</Option>
                <Option value="in_transit">In Transit</Option>
                <Option value="customs_pending_clearance">
                  Customs Pending
                </Option>
                <Option value="customs_cleared">Customs Cleared</Option>
                <Option value="delivered">Delivered</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="archived">Archived</Option>
              </Select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, color: "#8c8c8c" }}>Customer</label>
              <Select
                style={{ width: 200 }}
                placeholder="All Customers"
                allowClear
                showSearch
                value={companyFilter}
                onChange={setCompanyFilter}
                filterOption={(input, option) =>
                  (option?.children as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase()) || false
                }
              >
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </div>
          </FiltersContainer>

          {loading ? (
            <CenteredSpin>
              <Spin size="large" />
            </CenteredSpin>
          ) : (
            <>
              {/* Summary Cards */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Total Bookings"
                      value={summaryStats.totalBookings}
                      prefix={<BookOutlined />}
                    />
                    <div
                      style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}
                    >
                      Air: {summaryStats.airfreightBookings} | Sea:{" "}
                      {summaryStats.seafreightBookings}
                    </div>
                  </StatsCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Total CBM"
                      value={summaryStats.totalCBM.toFixed(2)}
                      precision={2}
                      suffix="m³"
                      prefix={<InboxOutlined />}
                    />
                    <div
                      style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}
                    >
                      Air: {summaryStats.airfreightCBM.toFixed(2)} m³ | Sea:{" "}
                      {summaryStats.seafreightCBM.toFixed(2)} m³
                    </div>
                  </StatsCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Total Weight"
                      value={summaryStats.totalKG.toFixed(2)}
                      precision={2}
                      suffix="kg"
                      prefix={<ShoppingOutlined />}
                    />
                    <div
                      style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}
                    >
                      Air: {summaryStats.airfreightKG.toFixed(2)} kg | Sea:{" "}
                      {summaryStats.seafreightKG.toFixed(2)} kg
                    </div>
                  </StatsCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Avg Booking Size"
                      value={summaryStats.avgCBM.toFixed(2)}
                      precision={2}
                      suffix="m³"
                      prefix={<LineChartOutlined />}
                    />
                    <div
                      style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}
                    >
                      Avg Weight: {summaryStats.avgKG.toFixed(2)} kg
                    </div>
                  </StatsCard>
                </Col>
              </Row>

              {/* Charts Row */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                  <DashboardCard
                    title={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <span>Daily Bookings</span>
                        <Space>
                          <Select
                            value={groupingPeriod}
                            onChange={(value) => setGroupingPeriod(value)}
                            size="small"
                            style={{ width: 80 }}
                          >
                            <Option value="day">Day</Option>
                            <Option value="week">Week</Option>
                            <Option value="month">Month</Option>
                          </Select>
                          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                            Stack
                          </span>
                          <Switch
                            checked={isStacked}
                            onChange={setIsStacked}
                            size="small"
                          />
                        </Space>
                      </div>
                    }
                  >
                    <ChartContainer>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dailyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tickFormatter={(value) => {
                              if (groupingPeriod === "day") {
                                return dayjs(value).format("MMM DD");
                              } else if (groupingPeriod === "week") {
                                return dayjs(value).format("MMM DD");
                              } else {
                                return dayjs(value).format("MMM YYYY");
                              }
                            }}
                          />
                          <YAxis />
                          <RechartsTooltip
                            labelFormatter={(value) => {
                              if (groupingPeriod === "day") {
                                return dayjs(value).format("MMM DD, YYYY");
                              } else if (groupingPeriod === "week") {
                                const weekStart = dayjs(value).startOf("week");
                                const weekEnd = dayjs(value).endOf("week");
                                return `${weekStart.format(
                                  "MMM DD"
                                )} - ${weekEnd.format("MMM DD, YYYY")}`;
                              } else {
                                return dayjs(value).format("MMMM YYYY");
                              }
                            }}
                            content={({ active, payload, label }: any) => {
                              if (!active || !payload || !payload.length) {
                                return null;
                              }

                              const data = payload[0]?.payload;
                              const airfreight = data?.airfreight || 0;
                              const seafreight = data?.seafreight || 0;
                              const total = airfreight + seafreight;

                              let labelText: string;
                              if (groupingPeriod === "day") {
                                labelText = dayjs(label).format("MMM DD, YYYY");
                              } else if (groupingPeriod === "week") {
                                const weekStart = dayjs(label).startOf("week");
                                const weekEnd = dayjs(label).endOf("week");
                                labelText = `${weekStart.format(
                                  "MMM DD"
                                )} - ${weekEnd.format("MMM DD, YYYY")}`;
                              } else {
                                labelText = dayjs(label).format("MMMM YYYY");
                              }

                              return (
                                <div
                                  style={{
                                    backgroundColor: "#fff",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    padding: "10px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                  }}
                                >
                                  <p
                                    style={{
                                      marginBottom: "8px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {labelText}
                                  </p>
                                  {payload.map((entry: any, index: number) => (
                                    <p
                                      key={index}
                                      style={{
                                        margin: "4px 0",
                                        color: entry.color,
                                      }}
                                    >
                                      {entry.name}: {entry.value}
                                    </p>
                                  ))}
                                  <p
                                    style={{
                                      marginTop: "8px",
                                      paddingTop: "8px",
                                      borderTop: "1px solid #e8e8e8",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Total: {total}
                                  </p>
                                </div>
                              );
                            }}
                          />
                          <Legend />
                          <Bar
                            dataKey="airfreight"
                            name="Airfreight"
                            fill={AIRFREIGHT_COLOR}
                            stackId={isStacked ? "bookings" : undefined}
                          />
                          <Bar
                            dataKey="seafreight"
                            name="Seafreight"
                            fill={SEAFREIGHT_COLOR}
                            stackId={isStacked ? "bookings" : undefined}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </DashboardCard>
                </Col>
                <Col xs={24} lg={12}>
                  <DashboardCard title="Transport Type Distribution">
                    <ChartContainer>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={transportDistribution}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) =>
                              `${name}: ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {transportDistribution.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  index === 0
                                    ? AIRFREIGHT_COLOR
                                    : SEAFREIGHT_COLOR
                                }
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </DashboardCard>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                  <DashboardCard title="Daily CBM Volume">
                    <ChartContainer>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dailyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tickFormatter={(value) => {
                              if (groupingPeriod === "day") {
                                return dayjs(value).format("MMM DD");
                              } else if (groupingPeriod === "week") {
                                return dayjs(value).format("MMM DD");
                              } else {
                                return dayjs(value).format("MMM YYYY");
                              }
                            }}
                          />
                          <YAxis />
                          <RechartsTooltip
                            labelFormatter={(value) => {
                              if (groupingPeriod === "day") {
                                return dayjs(value).format("MMM DD, YYYY");
                              } else if (groupingPeriod === "week") {
                                const weekStart = dayjs(value).startOf("week");
                                const weekEnd = dayjs(value).endOf("week");
                                return `${weekStart.format(
                                  "MMM DD"
                                )} - ${weekEnd.format("MMM DD, YYYY")}`;
                              } else {
                                return dayjs(value).format("MMMM YYYY");
                              }
                            }}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="airfreightCBM"
                            name="Airfreight CBM"
                            stackId="1"
                            stroke={AIRFREIGHT_COLOR}
                            fill={AIRFREIGHT_COLOR}
                            fillOpacity={0.6}
                          />
                          <Area
                            type="monotone"
                            dataKey="seafreightCBM"
                            name="Seafreight CBM"
                            stackId="1"
                            stroke={SEAFREIGHT_COLOR}
                            fill={SEAFREIGHT_COLOR}
                            fillOpacity={0.6}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </DashboardCard>
                </Col>
                <Col xs={24} lg={12}>
                  <DashboardCard title="Daily Weight (KG)">
                    <ChartContainer>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={dailyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tickFormatter={(value) => {
                              if (groupingPeriod === "day") {
                                return dayjs(value).format("MMM DD");
                              } else if (groupingPeriod === "week") {
                                return dayjs(value).format("MMM DD");
                              } else {
                                return dayjs(value).format("MMM YYYY");
                              }
                            }}
                          />
                          <YAxis />
                          <RechartsTooltip
                            labelFormatter={(value) => {
                              if (groupingPeriod === "day") {
                                return dayjs(value).format("MMM DD, YYYY");
                              } else if (groupingPeriod === "week") {
                                const weekStart = dayjs(value).startOf("week");
                                const weekEnd = dayjs(value).endOf("week");
                                return `${weekStart.format(
                                  "MMM DD"
                                )} - ${weekEnd.format("MMM DD, YYYY")}`;
                              } else {
                                return dayjs(value).format("MMMM YYYY");
                              }
                            }}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="airfreightKG"
                            name="Airfreight KG"
                            stackId="1"
                            stroke={AIRFREIGHT_COLOR}
                            fill={AIRFREIGHT_COLOR}
                            fillOpacity={0.6}
                          />
                          <Area
                            type="monotone"
                            dataKey="seafreightKG"
                            name="Seafreight KG"
                            stackId="1"
                            stroke={SEAFREIGHT_COLOR}
                            fill={SEAFREIGHT_COLOR}
                            fillOpacity={0.6}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </DashboardCard>
                </Col>
              </Row>

              {/* Top Customers and Route Performance */}
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <DashboardCard title="Top Customers">
                    <Table
                      dataSource={topCustomers}
                      rowKey="company"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: "Customer",
                          dataIndex: "company",
                          key: "company",
                        },
                        {
                          title: "Total Bookings",
                          dataIndex: "bookings",
                          key: "bookings",
                          align: "right",
                        },
                        {
                          title: "Airfreight",
                          dataIndex: "airfreightBookings",
                          key: "airfreightBookings",
                          align: "right",
                          render: (value: number) => (
                            <span style={{ color: AIRFREIGHT_COLOR }}>
                              {value}
                            </span>
                          ),
                        },
                        {
                          title: "Seafreight",
                          dataIndex: "seafreightBookings",
                          key: "seafreightBookings",
                          align: "right",
                          render: (value: number) => (
                            <span style={{ color: SEAFREIGHT_COLOR }}>
                              {value}
                            </span>
                          ),
                        },
                        {
                          title: "Total CBM",
                          dataIndex: "totalCBM",
                          key: "totalCBM",
                          align: "right",
                          render: (value: number) => value.toFixed(2),
                        },
                        {
                          title: "Total KG",
                          dataIndex: "totalKG",
                          key: "totalKG",
                          align: "right",
                          render: (value: number) => value.toFixed(2),
                        },
                      ]}
                    />
                  </DashboardCard>
                </Col>
                <Col xs={24} lg={12}>
                  <DashboardCard title="Route Performance">
                    <Table
                      dataSource={routePerformance}
                      rowKey="route"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: "Route",
                          dataIndex: "route",
                          key: "route",
                        },
                        {
                          title: "Bookings",
                          dataIndex: "bookings",
                          key: "bookings",
                          align: "right",
                        },
                        {
                          title: "Total CBM",
                          dataIndex: "totalCBM",
                          key: "totalCBM",
                          align: "right",
                          render: (value: number) => value.toFixed(2),
                        },
                        {
                          title: "Total KG",
                          dataIndex: "totalKG",
                          key: "totalKG",
                          align: "right",
                          render: (value: number) => value.toFixed(2),
                        },
                      ]}
                    />
                  </DashboardCard>
                </Col>
              </Row>
            </>
          )}
        </TabPane>

        <TabPane
          tab={
            <span>
              <ContainerOutlined /> Live Operations
            </span>
          }
          key="live"
        >
          {loadingContainers ? (
            <CenteredSpin>
              <Spin size="large" />
            </CenteredSpin>
          ) : (
            <>
              {/* Summary Stats for Live Operations */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Containers This Week"
                      value={
                        containers.filter((c) => {
                          if (!c.eta) return false;
                          const eta = dayjs(c.eta);
                          return (
                            eta.isAfter(dayjs().startOf("day")) &&
                            eta.isBefore(dayjs().endOf("week"))
                          );
                        }).length
                      }
                      prefix={<ContainerOutlined />}
                    />
                  </StatsCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Currently Unloading"
                      value={
                        containers.filter(
                          (c) => c.unloading_status === "in_progress"
                        ).length
                      }
                      prefix={<ClockCircleOutlined />}
                    />
                  </StatsCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Total Products"
                      value={containers.reduce(
                        (sum, c) => sum + c.total_products,
                        0
                      )}
                      prefix={<InboxOutlined />}
                    />
                  </StatsCard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <StatsCard>
                    <Statistic
                      title="Bookings with POs"
                      value={containers.reduce(
                        (sum, c) => sum + c.bookings_with_po,
                        0
                      )}
                      prefix={<FileTextOutlined />}
                    />
                  </StatsCard>
                </Col>
              </Row>

              {/* Containers List */}
              {containers.length === 0 ? (
                <DashboardCard>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#8c8c8c",
                    }}
                  >
                    No containers found for this week or currently being
                    unloaded
                  </div>
                </DashboardCard>
              ) : (
                containers.map((container) => (
                  <DashboardCard
                    key={container.id}
                    title={
                      <Space>
                        <span>
                          Container: {container.reference || `#${container.id}`}
                        </span>
                        {container.unloading_status && (
                          <Tag
                            color={
                              container.unloading_status === "in_progress"
                                ? "processing"
                                : container.unloading_status === "completed"
                                ? "success"
                                : "default"
                            }
                          >
                            {container.unloading_status === "in_progress"
                              ? "Unloading"
                              : container.unloading_status === "completed"
                              ? "Completed"
                              : "Pending"}
                          </Tag>
                        )}
                        {container.eta && (
                          <Tag
                            color={
                              dayjs(container.eta).isBefore(
                                dayjs().add(1, "day")
                              )
                                ? "red"
                                : "blue"
                            }
                          >
                            ETA: {dayjs(container.eta).format("MMM DD, YYYY")}
                          </Tag>
                        )}
                      </Space>
                    }
                    style={{ marginBottom: 16 }}
                  >
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Total Bookings"
                          value={container.total_bookings}
                          prefix={<BookOutlined />}
                        />
                      </Col>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Total Products"
                          value={container.total_products}
                          prefix={<InboxOutlined />}
                        />
                      </Col>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="With POs"
                          value={container.bookings_with_po}
                          valueStyle={{
                            color:
                              container.bookings_with_po > 0
                                ? "#52c41a"
                                : "#8c8c8c",
                          }}
                          prefix={<FileTextOutlined />}
                        />
                      </Col>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="With Labels"
                          value={container.bookings_with_labels}
                          valueStyle={{
                            color:
                              container.bookings_with_labels > 0
                                ? "#52c41a"
                                : "#8c8c8c",
                          }}
                          prefix={<PrinterOutlined />}
                        />
                      </Col>
                    </Row>

                    <Table
                      dataSource={container.bookings}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: "Booking",
                          dataIndex: "reference",
                          key: "reference",
                        },
                        {
                          title: "Customer",
                          dataIndex: "company_name",
                          key: "company_name",
                        },
                        {
                          title: "Status",
                          dataIndex: "status",
                          key: "status",
                          render: (status: string) => (
                            <Tag
                              color={
                                status === "delivered"
                                  ? "green"
                                  : status === "in_transit"
                                  ? "blue"
                                  : "default"
                              }
                            >
                              {status || "N/A"}
                            </Tag>
                          ),
                        },
                        {
                          title: "Unloading",
                          dataIndex: "unloading_status",
                          key: "unloading_status",
                          render: (status: string) => {
                            if (!status) return <Tag>Pending</Tag>;
                            const colorMap: Record<string, string> = {
                              pending: "default",
                              counting: "processing",
                              delivery_ready: "warning",
                              completed: "success",
                            };
                            return (
                              <Tag color={colorMap[status] || "default"}>
                                {status}
                              </Tag>
                            );
                          },
                        },
                        {
                          title: "Products",
                          dataIndex: "total_products",
                          key: "total_products",
                          align: "right",
                        },
                        {
                          title: "PO",
                          dataIndex: "has_po",
                          key: "has_po",
                          align: "center",
                          render: (has: boolean, record: any) => (
                            <Badge
                              count={record.po_count}
                              showZero
                              style={{
                                backgroundColor: has ? "#52c41a" : "#d9d9d9",
                              }}
                            />
                          ),
                        },
                        {
                          title: "Labels",
                          dataIndex: "has_shipping_labels",
                          key: "has_shipping_labels",
                          align: "center",
                          render: (has: boolean, record: any) => (
                            <Badge
                              count={record.shipping_label_count}
                              showZero
                              style={{
                                backgroundColor: has ? "#52c41a" : "#d9d9d9",
                              }}
                            />
                          ),
                        },
                      ]}
                    />
                  </DashboardCard>
                ))
              )}
            </>
          )}
        </TabPane>
      </Tabs>
    </Container>
  );
};

export default ReportsAdmin;
